import { Heading } from "components/Theme/heading";
import { assetTypeEnum, statusEnum } from "kubb";
import AssetRegisterList from "pages/assetRegister/AssetRegisterListPage/AssetRegisterList";
import type React from "react";
import RoutedTabGroupInterface from "../../../components/TabGroup/RoutedTabGroupInterface";
import {
  assetPositionToBreadcrumb,
  locationToBreadcrumb,
} from "../../../components/navigation/breadcrumbs/Breadcrumbs";
import { OneCardPage } from "../../../components/utils/OneCardPage";
import EventList from "../../../containers/event/EventList";
import NoteList from "../../../containers/notes/NotesList";
import type { AssetRegisterEntitySuccessProps } from "../../../core/action/GetAssetRegisterEntitiesCombination";
import { useUpdateAssetPosition } from "../../../hooks/updateEntity";

import type { Tab } from "../../../components/TabGroup/model.ts";
import IssuesList from "../../issue/IssueList/IssuesList.tsx";
import IntegrationHomePage from "../integrations/IntegrationHomePage";
import WirelessClusterGatewayAddList from "../wirelessCluster/WirelessClusterGatewayAddList";
import WirelessClusterGatewayList from "../wirelessCluster/WirelessClusterGatewayList";
import WirelessClusterReceiverAssignmentTab from "../wirelessCluster/WirelessClusterReceiverAssignmentTab";
import WirelessClusterReprocessTab from "../wirelessCluster/WirelessClusterReprocessTab";
import AssetPositionPreview from "./AssetPositionPreview";
import SimpleAssetRegisterUploadContainer from "./simpleAssetRegisterUpload/SimpleAssetRegisterUploadContainer";
import AssetPositionConfigurationTab from "./tabs/AssetPositionConfigurationTab";
import AssetPositionDeleteTab from "./tabs/AssetPositionDeleteTab";
import DeviceAssetPositionDashboard from "./tabs/DeviceAssetPositionDashboard.tsx";
import AssetPositionAssetHistoryTab from "./tabs/assetPositionAssetHistoryTab";

export default function AssetPositionDisplay({
  assetPosition,
  location,
  asset,
  loading,
  refresh,
}: AssetRegisterEntitySuccessProps): React.ReactElement {
  if (!assetPosition) {
    return <></>;
  }

  const deleted = assetPosition.status === statusEnum.DELETED;
  const gateway = assetPosition.asset_position_type === assetTypeEnum.gateway;
  const wireless_cluster =
    assetPosition.asset_position_type === assetTypeEnum.wireless_cluster;

  const update = useUpdateAssetPosition(assetPosition);

  const deleteAssetPosition = (): Promise<any> => {
    if (assetPosition) {
      return update.update({
        ...assetPosition,
        status: statusEnum.DELETED,
      });
    } else {
      throw new Error("Can not delete without an asset position");
    }
  };

  if (assetPosition.asset_position_type === assetTypeEnum.api_integration) {
    return (
      <IntegrationHomePage
        integration={{
          assetPosition,
          location,
          asset,
          loading,
          refresh,
        }}
        deleteAssetPosition={deleteAssetPosition}
      />
    );
  }
  const clusterGatewayTab = {
    tabName: "Cluster Gateways",
    tabPath: "gateways",
    tabContent: <WirelessClusterGatewayList clusterPosition={assetPosition} />,
  };
  const clusterReceiverAssignmentTab = {
    tabName: "Receiver Assignment",
    tabPath: "receiver_assignment",
    tabContent: (
      <WirelessClusterReceiverAssignmentTab clusterPosition={assetPosition} />
    ),
  };
  const clusterAddGatewaysTab = {
    tabName: "Add Cluster Gateways",
    tabPath: "add_gateways",
    tabContent: (
      <WirelessClusterGatewayAddList clusterPosition={assetPosition} />
    ),
    userTypes: "admin",
  };
  const clusterReprocessTab = {
    tabName: "Reprocess",
    tabPath: "reprocess",
    tabContent: <WirelessClusterReprocessTab clusterPosition={assetPosition} />,
    userTypes: "superadmin",
  };

  const ownedAssetPositionsTab = {
    tabName: "Owned Asset Positions",
    tabPath: "positions",
    tabContent: (
      <>
        <Heading>
          {gateway && "Gateway"}
          {wireless_cluster && "Cluster"} Owned Asset Positions
        </Heading>
        <AssetRegisterList
          params={{ parent_asset_position_id: assetPosition.asset_position_id }}
          company_id={assetPosition.company_id}
          scheme_id={assetPosition.scheme_id}
          createAssetTags={false}
        />
      </>
    ),
  };
  const dashboard = {
    tabName: "Dashboard",
    tabPath: "dashboard",
    tabContent: <DeviceAssetPositionDashboard assetPosition={assetPosition} />,
  };
  const issues = {
    tabName: "Issues",
    tabPath: "issues",
    tabContent: (
      <IssuesList
        companyId={assetPosition.company_id}
        assetPositionId={assetPosition.asset_position_id}
        includeClosed={true}
      />
    ),
  };
  const uploadAssetRegisterTab = {
    tabName: "Add Owned Asset Positions",
    tabPath: "add_positions",
    tabContent: (
      <>
        <Heading>Add Simple Asset Register</Heading>
        <SimpleAssetRegisterUploadContainer assetPosition={assetPosition} />
      </>
    ),
    userTypes: "admin",
    disabled: deleted,
    disabledPlaceholder: (
      <p className="text-gray-500 py-4">
        This asset position has been deleted. Upload is not available.
      </p>
    ),
  };
  const configuration = {
    tabName: "Configuration",
    tabPath: "configuration",
    tabContent: (
      <AssetPositionConfigurationTab
        assetPosition={assetPosition}
        refresh={refresh}
      />
    ),
    userTypes: "admin",
    disabled: !wireless_cluster && !gateway,
    disabledPlaceholder: (
      <p className="text-gray-500 py-4">
        Configuration is only available for gateway and wireless cluster asset
        positions.
      </p>
    ),
  };

  const assetHistoryTab = {
    tabName: "Asset History",
    tabPath: "link",
    tabContent: <AssetPositionAssetHistoryTab assetPosition={assetPosition} />,
  };
  const notesTab = {
    tabName: "Notes",
    tabPath: "note",
    tabContent: (
      <>
        <Heading>Notes</Heading>
        <NoteList assetPosition={assetPosition} />
      </>
    ),
  };
  const eventsTab = {
    tabName: "Events",
    tabPath: "events",
    tabContent: (
      <>
        <Heading>Asset Position Events</Heading>
        <EventList
          asset_position_id={assetPosition.asset_position_id}
          company_id={assetPosition.company_id}
        />
      </>
    ),
  };
  const deleteTab = {
    tabName: "Delete",
    tabPath: "delete",
    tabContent: (
      <AssetPositionDeleteTab
        assetPosition={assetPosition}
        delete={deleteAssetPosition}
        title={"Asset Position"}
      />
    ),
    userTypes: "admin",
  };
  const getTabs = (): Tab[] => {
    switch (assetPosition.asset_position_type) {
      case assetTypeEnum.gateway:
        return assetPosition.asset_id
          ? [
              dashboard,
              ownedAssetPositionsTab,
              uploadAssetRegisterTab,
              configuration,
              assetHistoryTab,
              notesTab,
              eventsTab,
              deleteTab,
            ]
          : [
              dashboard,
              assetHistoryTab,
              ownedAssetPositionsTab,
              uploadAssetRegisterTab,
              configuration,
              notesTab,
              eventsTab,
              deleteTab,
            ];
      case assetTypeEnum.wireless_cluster:
        return [
          clusterGatewayTab,
          clusterAddGatewaysTab,
          clusterReceiverAssignmentTab,
          clusterReprocessTab,
          ownedAssetPositionsTab,
          uploadAssetRegisterTab,
          configuration,
          notesTab,
          eventsTab,
          deleteTab,
        ];
      default:
        return [
          dashboard,
          issues,
          assetHistoryTab,
          notesTab,
          eventsTab,
          deleteTab,
        ];
    }
  };

  const niceRef =
    assetPosition.asset_position_reference ||
    `${assetPosition.asset_position_id.slice(0, 8)}...`;

  return (
    <OneCardPage
      headerTitle={
        wireless_cluster ? "Wireless Cluster Homepage" : "Asset Position Detail"
      }
      breadcrumbs={
        location && [
          locationToBreadcrumb(location),
          assetPositionToBreadcrumb(assetPosition),
        ]
      }
      loading={loading}
      setPageTitle={() => {
        return wireless_cluster
          ? `Wireless Cluster: ${niceRef}`
          : `Asset Position: ${niceRef}`;
      }}
    >
      {location && (
        <>
          <AssetPositionPreview
            assetPosition={assetPosition}
            asset={asset}
            location={location}
            refresh={refresh}
          />
          <RoutedTabGroupInterface
            tabs={getTabs()}
            topLevelPath={`/admin/asset_register/asset_position/${assetPosition.asset_position_id}`}
          />
        </>
      )}
    </OneCardPage>
  );
}
